/* =============================================================================
   Praesidus — U.S. Army landing page
   Behaviour for the usarmy-* sections. Vanilla JS, no dependencies.

   Loaded by usarmy-styles.liquid, which every section renders, so the file is
   present whichever bands a merchant keeps. Each unit below queries for its
   own hooks and returns quietly when that section is absent or switched off,
   and the whole file is re-runnable so the Theme Editor can reload a single
   section without double-binding.
   ========================================================================== */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  /* The Theme Editor swaps a section's markup and fires shopify:section:load,
     so every unit below runs again. `claim` marks an element the first time it
     is initialised and returns false afterwards, which keeps listeners and
     timers from stacking up on the bands that were not replaced. */
  function claim(el, name) {
    var key = "usarmyInit" + name;
    if (el.dataset[key]) return false;
    el.dataset[key] = "1";
    return true;
  }

  /* ---------------------------------------------------------------------
     Product rail
     A real horizontal scroller with a progress indicator: the thumb's width
     reports the visible fraction of the track and its offset reports the
     scroll position, so it spans the whole bar when every card already fits.
     Keyboard: Left/Right step one card, Home/End jump to either end.
     ------------------------------------------------------------------- */
  function initRail(rail) {
    if (!claim(rail, "Rail")) return;
    var track = rail.querySelector("[data-usarmy-rail-track]");
    if (!track) return;

    var section = rail.closest("section") || document;
    var progress = section.querySelector("[data-usarmy-rail-progress]");
    var thumb = section.querySelector("[data-usarmy-rail-thumb]");
    if (!progress || !thumb) return;

    /* Layout metrics are read only when they can actually change (load and
       resize); scrolling just repositions the thumb, which costs no layout. */
    var metrics = { scrollable: 0, thumbPct: 100 };

    function measure() {
      /* The indicator always occupies its designed slot so the vertical rhythm
         matches the comp; the thumb reports the visible fraction, which is the
         whole track once every card fits on screen. */
      var scrollable = Math.max(track.scrollWidth - track.clientWidth, 0);
      var visible = scrollable > 1 ? track.clientWidth / track.scrollWidth : 1;

      metrics.scrollable = scrollable;
      metrics.thumbPct = Math.min(Math.max(visible, 0.08), 1) * 100;

      thumb.style.width = metrics.thumbPct + "%";
      progress.setAttribute("data-scrollable", scrollable > 1 ? "true" : "false");
      position();
    }

    function position() {
      var ratio = metrics.scrollable > 1 ? track.scrollLeft / metrics.scrollable : 0;
      thumb.style.left = (ratio * (100 - metrics.thumbPct)) + "%";
    }

    function scrollTo(left) {
      track.scrollTo({
        left: left,
        behavior: reduceMotion.matches ? "auto" : "smooth"
      });
    }

    function step(dir) {
      var card = track.firstElementChild;
      if (!card) return;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({
        left: dir * (card.getBoundingClientRect().width + gap),
        behavior: reduceMotion.matches ? "auto" : "smooth"
      });
    }

    track.addEventListener("scroll", position, { passive: true });

    track.addEventListener("keydown", function (e) {
      if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
      else if (e.key === "Home") { e.preventDefault(); scrollTo(0); }
      else if (e.key === "End") { e.preventDefault(); scrollTo(track.scrollWidth); }
    });

    if ("ResizeObserver" in window) {
      new ResizeObserver(measure).observe(track);
    } else {
      window.addEventListener("resize", measure);
    }
    measure();
  }

  /* ---------------------------------------------------------------------
     Product rail — desktop "Show all watches"
     From 1192px up the rail is a three-column grid and the cards past the
     first row start hidden (see the CSS). Opening animates the track from
     its current height to its full height while the new cards fade up in
     sequence; closing runs the height back down to the first row and only
     then hides the extras. Below 1192px the toggle is hidden and the class
     changes have no visual effect, so the state simply carries over.
     ------------------------------------------------------------------- */
  var EXPAND_MS = 600; /* matches --usarmy-expand-dur's fallback in the CSS */

  function initRailExpand(rail) {
    if (!rail.classList.contains("usarmy-lp__rail--expandable")) return;
    if (!claim(rail, "RailExpand")) return;
    var track = rail.querySelector("[data-usarmy-rail-track]");
    var section = rail.closest("section") || document;
    var toggle = section.querySelector("[data-usarmy-rail-toggle]");
    if (!track || !toggle) return;

    var label = toggle.querySelector("[data-usarmy-rail-toggle-label]");
    var extras = Array.prototype.slice.call(track.querySelectorAll(".usarmy-lp__product--extra"));
    var desktop = window.matchMedia("(min-width: 1192px)");
    var pending = null;

    extras.forEach(function (li, i) { li.style.setProperty("--usarmy-i", i); });

    function isOpen() { return !rail.classList.contains("usarmy-lp__rail--collapsed"); }

    /* Ends any running height animation at once, running its completion. */
    function settle() {
      if (!pending) return;
      var p = pending;
      pending = null;
      clearTimeout(p.timer);
      track.removeEventListener("transitionend", p.onEnd);
      track.classList.remove("is-animating");
      track.style.height = "";
      if (p.done) p.done();
    }

    function animateHeight(from, to, done) {
      track.style.height = from + "px";
      track.classList.add("is-animating");
      void track.offsetHeight; /* commit the start height before moving */
      track.style.height = to + "px";
      var onEnd = function (e) {
        if (e.target === track && e.propertyName === "height") settle();
      };
      track.addEventListener("transitionend", onEnd);
      /* transitionend never fires if the height did not actually change */
      pending = { done: done, onEnd: onEnd, timer: setTimeout(settle, EXPAND_MS + 100) };
    }

    function setExpanded(open) {
      var from = track.getBoundingClientRect().height;
      settle();
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      if (label) label.textContent = open ? toggle.dataset.labelLess : toggle.dataset.labelMore;

      var animate = desktop.matches && !reduceMotion.matches;
      extras.forEach(function (li) { li.classList.remove("is-entering", "is-leaving"); });

      if (open) {
        rail.classList.remove("usarmy-lp__rail--collapsed");
        if (!animate) return;
        var to = track.getBoundingClientRect().height;
        extras.forEach(function (li) { li.classList.add("is-entering"); });
        animateHeight(from, to);
        return;
      }

      if (!animate) {
        rail.classList.add("usarmy-lp__rail--collapsed");
        return;
      }
      /* The first row ends one row-gap above the first extra card. */
      var gap = parseFloat(getComputedStyle(track).rowGap) || 0;
      var firstRow = extras[0].getBoundingClientRect().top - track.getBoundingClientRect().top - gap;
      extras.forEach(function (li) { li.classList.add("is-leaving"); });
      animateHeight(from, firstRow, function () {
        rail.classList.add("usarmy-lp__rail--collapsed");
        extras.forEach(function (li) { li.classList.remove("is-leaving"); });
        /* If the collapse pulled the first row above the viewport, bring it
           back so the visitor is not left looking at the next band. */
        if (track.getBoundingClientRect().top < 0) {
          track.scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth", block: "start" });
        }
      });
    }

    toggle.addEventListener("click", function () { setExpanded(!isOpen()); });

    /* Crossing the breakpoint mid-animation would leave a fixed height behind. */
    var onBreakpoint = function () { settle(); };
    if (desktop.addEventListener) desktop.addEventListener("change", onBreakpoint);
    else if (desktop.addListener) desktop.addListener(onBreakpoint);

    /* Theme Editor: selecting a hidden watch block opens the rail so the
       merchant can see the card they are editing. */
    rail.addEventListener("shopify:block:select", function (e) {
      if (!isOpen() && e.target.classList && e.target.classList.contains("usarmy-lp__product--extra")) {
        setExpanded(true);
      }
    });
  }



  /* ---------------------------------------------------------------------
     Caseback sequence (Section 04)

     Four plates, identical but for the engraving, dissolving one into the
     next. Not in the Figma comp — the four page variants each carry one of
     the engravings and the client asked for them to cycle.

     The incoming slide fades in ON TOP of the outgoing one, which holds full
     opacity until the fade ends; a symmetrical crossfade would dip to the
     band colour at the midpoint, and with four near-identical plates that
     dip would be the only movement anyone saw.

     It pauses when scrolled out of view, when the tab is hidden, and on
     request through the control. A visitor who asks for reduced motion gets
     the first plate and a play button instead of a slideshow.
     ------------------------------------------------------------------- */
  function initSequence(root) {
    if (!claim(root, "Seq")) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll("[data-usarmy-slide]"));
    if (slides.length < 2) return;          /* one plate: nothing to animate */

    var figure = root.closest(".usarmy-lp__record-figure") || root.parentNode;
    var toggle = figure ? figure.querySelector("[data-usarmy-sequence-toggle]") : null;
    var interval = (parseFloat(root.getAttribute("data-interval")) || 3.5) * 1000;
    var fade = parseFloat(root.getAttribute("data-fade")) || 900;

    var index = 0, timer = null, fadeTimer = null;
    var onScreen = true;

    /* The sequence loops for everyone by default — the client wants it to read
       as a looping image, the way a GIF would. Turning on "Respect reduced
       motion" in the Theme Editor makes it start paused for a visitor who has
       asked their system for less movement; the pause control is present
       either way, which is what WCAG 2.2.2 actually requires. */
    var honourRM = root.getAttribute("data-respect-reduced-motion") === "true";
    var wanted = !(honourRM && reduceMotion.matches);

    function show(next) {
      if (next === index) return;
      var from = slides[index], to = slides[next];
      from.classList.add("is-prev");
      from.classList.remove("is-active");
      to.classList.add("is-active");
      index = next;
      window.clearTimeout(fadeTimer);
      fadeTimer = window.setTimeout(function () {
        slides.forEach(function (s) {
          if (s !== slides[index]) s.classList.remove("is-prev");
        });
      }, fade + 40);
    }

    function tick() { show((index + 1) % slides.length); }

    function running() { return wanted && onScreen && !document.hidden; }

    function sync() {
      window.clearInterval(timer);
      timer = null;
      if (running()) timer = window.setInterval(tick, interval);
      if (toggle) {
        toggle.setAttribute("data-paused", wanted ? "false" : "true");
        toggle.setAttribute("aria-label",
          wanted ? toggle.getAttribute("data-label-pause") || "Pause the image sequence"
                 : toggle.getAttribute("data-label-play") || "Play the image sequence");
      }
    }

    if (toggle) {
      toggle.hidden = false;
      toggle.addEventListener("click", function () {
        wanted = !wanted;
        /* When the sequence is deliberately held still for a reduced-motion
           visitor, a press steps one plate instead of starting the loop, so
           the control still does something visible. */
        if (wanted && honourRM && reduceMotion.matches) { tick(); wanted = false; }
        sync();
      });
    }

    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        sync();
      }, { rootMargin: "120px" }).observe(root);
    }

    document.addEventListener("visibilitychange", sync);
    if (honourRM && reduceMotion.addEventListener) {
      reduceMotion.addEventListener("change", function () {
        wanted = !reduceMotion.matches;
        sync();
      });
    }

    sync();
  }

  /* ---------------------------------------------------------------------
     Field notes (testimonial audio)

     The Figma file ships no audio asset, so nothing is invented here.
     Add `data-audio="…"` to a button and it plays that clip; without one
     the control reports, accessibly, that the recording is not available.
     Only one note plays at a time.
     ------------------------------------------------------------------- */
  function initFieldNotes() {
    var buttons = Array.prototype.slice.call(
      document.querySelectorAll("[data-usarmy-fieldnote]")
    );
    if (!buttons.length) return;

    var status = document.querySelector(".usarmy-lp__notes-status");
    var current = null;

    function announce(msg) {
      if (status) status.textContent = msg || "";
    }

    function stop(btn) {
      btn.setAttribute("aria-pressed", "false");
      var label = btn.querySelector(".usarmy-lp__note-label");
      if (label && btn._idleLabel) label.firstChild.nodeValue = btn._idleLabel;
      if (btn._audio) { btn._audio.pause(); btn._audio.currentTime = 0; }
      if (current === btn) current = null;
    }

    buttons.forEach(function (btn) {
      if (!claim(btn, "Note")) return;
      var label = btn.querySelector(".usarmy-lp__note-label");
      btn._idleLabel = label ? label.firstChild.nodeValue : "";

      var src = btn.getAttribute("data-audio");
      if (src) {
        btn._audio = new Audio(src);
        btn._audio.addEventListener("ended", function () { stop(btn); });
      }

      btn.addEventListener("click", function () {
        var playing = btn.getAttribute("aria-pressed") === "true";

        if (current && current !== btn) stop(current);

        if (playing) {
          stop(btn);
          announce("");
          return;
        }

        if (!btn._audio) {
          announce("That field note recording isn’t available yet.");
          return;
        }

        btn.setAttribute("aria-pressed", "true");
        if (label) label.firstChild.nodeValue = "Pause field note · ";
        current = btn;
        announce("");
        btn._audio.play().catch(function () {
          stop(btn);
          announce("That field note could not be played.");
        });
      });
    });
  }


  function initAll() {
    document.querySelectorAll("[data-usarmy-rail]").forEach(initRail);
    document.querySelectorAll("[data-usarmy-rail]").forEach(initRailExpand);
    document.querySelectorAll("[data-usarmy-sequence]").forEach(initSequence);
    initFieldNotes();
  }

  initAll();
  /* the Theme Editor replaces one section's markup at a time */
  document.addEventListener("shopify:section:load", initAll);

  /* ---------------------------------------------------------------------
     Anchor links — respect reduced motion, and move focus to the target
     so keyboard users land where the page just scrolled.
     ------------------------------------------------------------------- */
  document.addEventListener("click", function (e) {
    var link = e.target.closest('a[href^="#"]');
    if (!link) return;

    var id = link.getAttribute("href").slice(1);
    if (!id) return;

    var target = document.getElementById(id);
    if (!target) return;

    e.preventDefault();
    target.scrollIntoView({
      behavior: reduceMotion.matches ? "auto" : "smooth",
      block: "start"
    });

    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });

    if (history.replaceState) history.replaceState(null, "", "#" + id);
  });
})();
